import type { ContentSlideData, ContentElement } from "@/types/lesson";

/**
 * Verified keyword → Noto Emoji Lottie codepoint (hex, no 0x).
 * Prefer only these — unlisted assets often 404 on gstatic.
 */
export const KEYWORD_TO_CODEPOINT: Record<string, string> = {
  elephant: "1f418",
  cat: "1f431",
  dog: "1f436",
  rabbit: "1f430",
  frog: "1f438",
  fox: "1f98a",
  bear: "1f43b",
  lion: "1f981",
  bird: "1f426",
  fish: "1f41f",
  butterfly: "1f98b",
  bee: "1f41d",
  turtle: "1f422",
  penguin: "1f427",
  duck: "1f986",
  horse: "1f434",
  cow: "1f404",
  pig: "1f437",
  monkey: "1f435",
  apple: "1f34e",
  banana: "1f34c",
  cake: "1f382",
  pizza: "1f355",
  bread: "1f35e",
  carrot: "1f955",
  strawberry: "1f353",
  egg: "1f95a",
  sun: "1f31e",
  moon: "1f319",
  star: "2b50",
  rainbow: "1f308",
  flower: "1f338",
  tree: "1f333",
  cloud: "2601",
  rain: "1f327",
  fire: "1f525",
  water: "1f4a7",
  earth: "1f30d",
  ocean: "1f30a",
  lightning: "26a1",
  snow: "2744",
  book: "1f4da",
  pencil: "270f",
  school: "1f3eb",
  backpack: "1f392",
  balloon: "1f388",
  ball: "26bd",
  microscope: "1f52c",
  rocket: "1f680",
  atom: "269b",
  dna: "1f9ec",
  computer: "1f4bb",
  clock: "1f550",
  house: "1f3e0",
  music: "1f3b5",
  car: "1f697",
  train: "1f682",
  airplane: "2708",
  celebration: "1f389",
  trophy: "1f3c6",
  heart: "2764",
  vocabulary: "1f4da",
  reading: "1f4da",
  writing: "270f",
  science: "1f52c",
  history: "1f4da",
  geography: "1f30d",
  maths: "269b",
  math: "269b",
  snake: "1f40d",
};

export function notoEmojiLottieUrl(codepoint: string): string {
  const cp = codepoint.toLowerCase().replace(/^0x/, "");
  return `https://fonts.gstatic.com/s/e/notoemoji/latest/${cp}/lottie.json`;
}

/** Alternate Noto paths when primary asset is missing (404). */
export function lottieFallbackUrlsForCodepoint(primary: string): string[] {
  const p = primary.toLowerCase().replace(/^0x/, "");
  const bookAlts = ["1f4da", "1f4d5", "1f4d6"].filter((c) => c !== p).map(notoEmojiLottieUrl);
  const map: Record<string, string[]> = {
    "1f4da": bookAlts,
    "1f4d5": bookAlts,
    "1f4d6": bookAlts,
  };
  return map[p] ?? [];
}

function collectSlideText(slide: ContentSlideData): string {
  const chunks: string[] = [];
  chunks.push(slide.title || "");
  chunks.push(slide.narration || "");
  if (!slide.elements) return chunks.join(" ");
  for (const el of slide.elements) {
    chunks.push(...elementTextChunks(el));
  }
  return chunks.join(" ");
}

function elementTextChunks(el: ContentElement): string[] {
  switch (el.type) {
    case "text":
      return [el.content];
    case "term":
      return [el.term, el.definition, el.icon || ""];
    case "example":
      return [el.label || "", el.content];
    case "steps":
      return [
        el.title || "",
        ...(el.items || []).map((item) =>
          typeof item === "string" ? item : `${item.icon || ""} ${item.text || ""}`
        ),
      ];
    case "tip":
      return [el.text, el.icon || ""];
    case "bar_chart":
    case "line_chart":
      return [el.title || "", ...el.labels, String(el.values)];
    case "pie_chart":
      return [el.title || "", ...el.labels];
    case "comparison_table":
      return [el.title || "", ...el.headers, ...el.rows.flat()];
    case "process_flow":
      return [el.title || "", ...el.steps.map((s) => `${s.label} ${s.detail} ${s.icon || ""}`)];
    case "labeled_diagram":
      return [el.title || "", el.description || "", ...el.parts.map((p) => `${p.label} ${p.detail}`)];
    default:
      return [];
  }
}

/** Earliest whole-word match wins (document order), then longer keyword at same index */
export function findFirstKeywordInText(blob: string): { keyword: string; codepoint: string } | null {
  const lower = blob.toLowerCase();
  let bestIndex = Infinity;
  let best: { keyword: string; codepoint: string } | null = null;

  const keywords = Object.keys(KEYWORD_TO_CODEPOINT).sort((a, b) => b.length - a.length);

  for (const keyword of keywords) {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const r = new RegExp(`\\b${escaped}\\b`, "i");
    const m = r.exec(lower);
    if (m && m.index !== undefined) {
      const idx = m.index;
      if (
        idx < bestIndex ||
        (idx === bestIndex && keyword.length > (best?.keyword.length ?? 0))
      ) {
        bestIndex = idx;
        best = { keyword, codepoint: KEYWORD_TO_CODEPOINT[keyword] };
      }
    }
  }
  return best;
}

export type SlideVisualResult =
  | { kind: "lottie"; codepoint: string; keyword: string }
  | { kind: "none" };

export function findSlideVisual(slide: ContentSlideData): SlideVisualResult {
  if (slide.image?.unsplash_query?.trim()) {
    return { kind: "none" };
  }

  if (slide.lottie_ref?.trim()) {
    const key = slide.lottie_ref.toLowerCase().trim();
    const cp = KEYWORD_TO_CODEPOINT[key];
    if (cp) {
      return { kind: "lottie", codepoint: cp, keyword: key };
    }
    if (/^[0-9a-f]{2,8}$/i.test(key)) {
      return { kind: "lottie", codepoint: key.toLowerCase(), keyword: key };
    }
  }

  const blob = collectSlideText(slide);
  const found = findFirstKeywordInText(blob);
  if (found) {
    return { kind: "lottie", codepoint: found.codepoint, keyword: found.keyword };
  }
  return { kind: "none" };
}
